/**
 * /admin/health - system health operator dashboard.
 *
 * Per CHAP-176 (Phase 1N #148). Read-only, admin-gated.
 *
 * Refresh: every 5 min by default; manual refresh button. Server-side
 * cache is 60s so consecutive refreshes don't hammer R2 list calls.
 */

import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../lib/api.js';

interface DepCheck {
  status: 'ok' | 'fail' | 'skipped' | 'error' | 'unconfigured';
  reason?: string;
}

interface HealthSnapshot {
  generated_at: string;
  cached: boolean;
  db: {
    tables: Record<string, number>;
    total_rows: number;
  };
  r2: DepCheck & {
    total_bytes: number;
    total_objects: number;
    per_tenant: Array<{ tenant: string; bytes: number; objects: number }>;
  };
  stripe: { status: string; webhook_events_24h: number; last_event_at: string | null };
  sentry: { status: string; note: string };
  alerts: Array<{ metric: string; level: 'warn' | 'critical'; message: string }>;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export default function AdminHealth() {
  const { data, isLoading, error, refetch, isRefetching } = useQuery<
    HealthSnapshot,
    Error
  >({
    queryKey: ['admin-health'],
    queryFn: () => apiGet<HealthSnapshot>('/api/admin/health'),
    refetchInterval: 5 * 60_000,
  });

  return (
    <div className="space-y-4 min-w-0">
      <header className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <div className="flex items-start justify-between gap-3 min-w-0">
          <div className="flex-1 min-w-0">
            <h2 className="text-navy text-xl sm:text-2xl font-bold">System Health</h2>
            <p className="text-neutral text-sm mt-1">
              Operator dashboard. Per CHAP-176.{' '}
              {data?.generated_at && (
                <span className="text-xs">
                  Last updated {new Date(data.generated_at).toLocaleTimeString()}
                  {data.cached && ' (cached)'}
                </span>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isRefetching}
            className="text-sm font-medium px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 flex-shrink-0"
          >
            {isRefetching ? '…' : 'Refresh'}
          </button>
        </div>
      </header>

      {isLoading && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center text-neutral text-sm">
          Loading system health…
        </div>
      )}

      {error && (
        <div className="border border-red-200 bg-red-50 text-red-800 text-sm rounded-xl p-3">
          Failed to load: {error.message}
        </div>
      )}

      {data?.alerts && data.alerts.length > 0 && (
        <section className="space-y-2">
          {data.alerts.map((a, i) => (
            <div
              key={i}
              className={`border rounded-xl p-3 text-sm flex items-start gap-2 min-w-0 ${
                a.level === 'critical'
                  ? 'border-red-200 bg-red-50 text-red-900'
                  : 'border-amber-200 bg-amber-50 text-amber-900'
              }`}
            >
              <WarnIcon />
              <div className="min-w-0">
                <strong className="uppercase text-xs tracking-wide">
                  {a.level === 'critical' ? 'Critical' : 'Warning'}
                </strong>{' '}
                <span className="font-mono text-xs">{a.metric}</span>: {a.message}
              </div>
            </div>
          ))}
        </section>
      )}

      {data && (
        <>
          <Section title="Database">
            <p className="text-xs text-neutral mb-3">
              {data.db.total_rows.toLocaleString()} total rows across{' '}
              {Object.keys(data.db.tables).length} tracked tables
            </p>
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-neutral">
                <tr className="border-b border-gray-100">
                  <th className="pb-2 pr-3 font-medium">Table</th>
                  <th className="pb-2 text-right font-medium">Rows</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(data.db.tables)
                  .sort(([, a], [, b]) => b - a)
                  .map(([table, n]) => (
                    <tr key={table} className="border-b border-gray-50">
                      <td className="py-1.5 font-mono text-xs">{table}</td>
                      <td className="py-1.5 text-right font-mono">
                        {n >= 0 ? n.toLocaleString() : '(error)'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </Section>

          <Section title="Cloudflare R2 storage">
            <div className="flex items-center justify-between gap-3 mb-3">
              <p className="text-xs text-neutral">
                {data.r2.status === 'ok'
                  ? `${formatBytes(data.r2.total_bytes)} across ${data.r2.total_objects.toLocaleString()} objects`
                  : `Status: ${data.r2.status}${data.r2.reason ? ' - ' + data.r2.reason : ''}`}
              </p>
              {data.r2.status === 'ok' && (
                <span className="text-xs text-neutral">
                  {((data.r2.total_bytes / (10 * 1024 * 1024 * 1024)) * 100).toFixed(1)}%
                  of 10GB free tier
                </span>
              )}
            </div>
            {data.r2.status === 'ok' && data.r2.per_tenant.length > 0 && (
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-neutral">
                  <tr className="border-b border-gray-100">
                    <th className="pb-2 pr-3 font-medium">Tenant</th>
                    <th className="pb-2 text-right font-medium pr-3">Size</th>
                    <th className="pb-2 text-right font-medium">Objects</th>
                  </tr>
                </thead>
                <tbody>
                  {data.r2.per_tenant.map((t) => (
                    <tr key={t.tenant} className="border-b border-gray-50">
                      <td className="py-1.5 font-mono text-xs">{t.tenant}</td>
                      <td className="py-1.5 text-right font-mono pr-3">
                        {formatBytes(t.bytes)}
                      </td>
                      <td className="py-1.5 text-right font-mono">{t.objects}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          <Section title="Stripe webhooks">
            <p className="text-sm">
              {data.stripe.webhook_events_24h.toLocaleString()} events in last 24h
              {data.stripe.last_event_at && (
                <span className="text-xs text-neutral ml-2">
                  · last:{' '}
                  {new Date(data.stripe.last_event_at).toLocaleString()}
                </span>
              )}
            </p>
          </Section>

          <Section title="Sentry">
            <p className="text-sm text-neutral italic">{data.sentry.note}</p>
          </Section>

          <Section title="External dashboards">
            <ul className="space-y-2 text-sm">
              <li>
                <ExternalLink
                  href="https://dashboard.render.com/web/srv-d04qjvbe5dus73c2u3qg"
                  label="Render service (chapterbuild-web)"
                />
              </li>
              <li>
                <ExternalLink
                  href="https://sentry.io/organizations/jake-gannon/projects/chapterbuild/"
                  label="Sentry project (chapterbuild)"
                />
              </li>
              <li>
                <ExternalLink
                  href="https://dashboard.stripe.com/dashboard"
                  label="Stripe dashboard (1116 LLC; filter brand=chapterbuild)"
                />
              </li>
              <li>
                <ExternalLink
                  href="https://dash.cloudflare.com/?to=/:account/r2/default/buckets/chapterbuild-dataroom"
                  label="Cloudflare R2 bucket (chapterbuild-dataroom)"
                />
              </li>
              <li>
                <ExternalLink
                  href="https://betteruptime.com/"
                  label="Better Uptime monitors (post CHAP-264.6)"
                />
              </li>
            </ul>
          </Section>
        </>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 min-w-0">
      <h3 className="text-navy text-sm font-semibold mb-3">{title}</h3>
      <div className="min-w-0 overflow-x-auto">{children}</div>
    </section>
  );
}

function ExternalLink({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 text-navy hover:text-navy/80 hover:underline min-w-0"
    >
      <span className="truncate">{label}</span>
      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
      </svg>
    </a>
  );
}

function WarnIcon() {
  return (
    <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
  );
}
